import Experience from "./Experience";
import Events from "./Utils/Events";
import gsap from "gsap";
import Swiper from "swiper";
import { Pagination, Autoplay, Keyboard } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

/* 
  This class manages the video carousel functionality, including initialization, playback control, and event handling.
  It uses the Swiper library for carousel functionality and GSAP for animations.
*/
class VideoCarousel {
  constructor() {
    this.experience = new Experience();
    this.swiper = null;
    this.loader = this.experience.loader;
    this.userPersona = this.experience.state.persona;
    this.videoCarousel = document.querySelector(".swiper");
    this.personaVideos = [];
    this.initialSlide = null;
    this.currentVideoIndex = null;
    this.playbackButtons = document.querySelectorAll(".persona__video__button");

    this.shareButtons = document.querySelectorAll("[data-show=dropdown]");
    this.dropdowns = document.querySelectorAll(
      ".persona__overlay__share__dropdown"
    );

    Events.subscribe("content.updated", () => {
      this.getPersonaVideoElements();
    });

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.shareButtons.forEach((button) => {
      button.addEventListener("click", this.handleDropdown.bind(this));
    });

    this.dropdowns.forEach((dropdown) => {
      dropdown.addEventListener("click", this.hideDropdowns.bind(this));
    });

    document.addEventListener("click", (event) => {
      // if a dropdown is open and the click is outside the dropdown, close it
      if (event.target.closest(".persona__overlay__share") === null) {
        this.hideDropdowns();
      }
    });

    this.playbackButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const persona = button.closest(".persona").getAttribute("data-persona");
        this.togglePlayback(persona, button);
      });
    });
  }

  handleDropdown(event) {
    const button = event.currentTarget;
    const dropdown = button.nextElementSibling;

    // Hide all dropdowns if you want only one open at a time
    this.hideDropdowns();

    // Toggle the 'open' class
    dropdown.classList.toggle("open");

    if (dropdown.classList.contains("open")) {
      button.style.visibility = "hidden";
    } else {
      button.style.visibility = "visible";
    }
  }

  hideDropdowns() {
    this.dropdowns.forEach((dropdown) => {
      dropdown.classList.remove("open");
    });

    this.shareButtons.forEach((button) => {
      button.style.visibility = "visible";
    });
  }

  initializeSwiper(initialSlide) {
    this.initialSlide = initialSlide;

    console.log("Initializing Swiper with initial slide:", initialSlide);

    this.swiper = new Swiper(".swiper", {
      initialSlide: initialSlide,
      speed: 1100,
      loop: true,
      grabCursor: true,
      slideToClickedSlide: true,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      slidesPerView: 1,
      spaceBetween: 40,
      centeredSlides: true,
      a11y: {
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide",
      },
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
      modules: [Pagination, Autoplay, Keyboard],
      breakpoints: {
        768: {
          slidesPerView: 1.4,
          spaceBetween: 0,
        },
        1440: {
          slidesPerView: 2.2,
        },

        1920: {
          slidesPerView: 2.5,
        },
      },
      on: {
        slideChangeTransitionStart: () => {
          if (this.swiper) {
            // Begin opacity transition at slide change start
            this.startOpacityTransition(this.swiper.realIndex);
          }
        },
        transitionEnd: () => {
          if (this.swiper) {
            // Play video once the transition completes
            this.playVideo(this.swiper.realIndex);
            this.hideDropdowns();
          }
        },
      },
    });

    // Show the video carousel after the content has been updated
    Events.subscribe("content.updated", this.show.bind(this));
  }

  show() {
    gsap.to(this.videoCarousel, {
      duration: 1,
      autoAlpha: 1,
    });
  }

  getPersonaVideoElements() {
    this.personaVideos = this.experience.contentUpdater.personaVideos;
    this.playVideo(this.initialSlide);
  }

  startOpacityTransition(index) {
    // Fade out all videos except the one at the specified index
    this.personaVideos.forEach((video, idx) => {
      gsap.to(video, {
        duration: 0.5,
        opacity: idx === index ? 0 : 0.3, // Fade in active video and fade out others
      });
    });
  }

  playVideo(index) {
    // TODO - fix issues with video opacities
    // Skip if the index is the same as the current one
    if (this.currentVideoIndex === index) {
      return;
    }

    // pause all videos except the one at the specified index
    this.personaVideos.forEach((video, idx) => {
      // video.style.opacity = 0.3;
      gsap.to(video, {
        duration: 0.5,
        opacity: 0.3,
      });

      if (idx !== index) {
        const handlePause = () => {
          if (video.currentTime >= 4.5) {
            video.pause();
            video.currentTime = 4.5;
            video.removeEventListener("timeupdate", handlePause);
          }
        };

        if (video.currentTime < 4.5) {
          video.addEventListener("timeupdate", handlePause);
        } else {
          video.pause();
          video.currentTime = 4.5;
        }
      }
    });

    if (this.personaVideos[index]) {
      let video = this.personaVideos[index];
      video.currentTime = 0.05;
      gsap.to(video, {
        duration: 0.5,
        opacity: 1,
        delay: 0.1,

        onComplete: async () => {
          video.loop = true;
          // Handle exceptions for playing video
          // Safari can block the playing of the video, throwing an exception
          try {
            // Await the promise to ensure exceptions can be caught
            await video.play();
          } catch (exception) {
            // console.error(exception);
          }

          // play the video until the 4.5s mark
          // const handlePause = () => {
          //   if (video.currentTime >= 4.5) {
          //     video.pause();
          //     video.currentTime = 4.5;
          //     video.removeEventListener("timeupdate", handlePause);
          //   }
          // };

          // video.addEventListener("timeupdate", handlePause);
        },
      });
      this.currentVideoIndex = index; // Update the current index
    } else {
      console.log("Video not found at index:", index);
    }
  }

  togglePlayback(persona, button) {
    const section = document.querySelector(
      `.persona[data-persona="${persona}"]`
    );
    const video = section.querySelector("video");
    const icon = button.querySelector("img");

    if (video.paused) {
      video.play();
      icon.src = "/assets/images/icon-pause.svg";
      button.setAttribute("aria-label", "Pause video");
    } else {
      video.pause();
      icon.src = "/assets/images/icon-play.svg";
      button.setAttribute("aria-label", "Play video");
      // video.currentTime = 4.5;
    }
  }
}

export default VideoCarousel;
